import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import { useObserver } from 'mobx-react-lite'
import { FC } from 'react'
import { I18N } from 'utils/localisation'

import { IFreightieJSON, TActor, TUser } from '../../Actors'
import { INITIALS_AVATAR, THUMBNAIL_AVATAR } from '../../avatars/AvatarType'
import { getUserType } from '../../avatars/avatarUtils'
import { SubscriptionAvatar } from '../../avatars/SubscriptionAvatar'
import { TIdType, TSubscription } from '../SubscriptionModel'
import { MembersAutoComplete } from './MembersAutoComplete'

export const testIds = { subscriberItem: 'subscriber-list_item' }

const getStyles = (theme: Theme) => ({
  autoComplete: {
    padding: theme.spacing(2, 0),
  },
  subHeading: {
    marginBottom: 0,
    fontWeight: theme.typography.fontWeightRegular,
  },
  listItem: {
    paddingLeft: 0,
  },
})

export interface MembersEditProps {
  title: string
  open: boolean
  handleClose: () => void
  subscription: TSubscription
  addSubscriber: (id: string, idType: TIdType) => Promise<void>
  removeSubscriber: (id: string, idType: TIdType) => void
  users: TUser[]
  getFreighties?: (name: string) => Promise<IFreightieJSON[]>
}

export const MembersEdit: FC<MembersEditProps> = ({
  open,
  handleClose,
  title,
  subscription,
  subscription: { subscribers },
  removeSubscriber,
  addSubscriber,
  users,
  getFreighties,
}) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return useObserver(() => (
    <Dialog fullScreen={fullScreen} open={open} onClose={handleClose} maxWidth="sm">
      <DialogTitle showClose onClose={handleClose}>
        <I18N term={title} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <I18N term="members-edit-sub_title" />
        </DialogContentText>
        <Box sx={styles.autoComplete}>
          <MembersAutoComplete
            subscription={subscription}
            addSubscriber={addSubscriber}
            users={users}
            getFreighties={getFreighties}
          />
        </Box>
        <Typography variant="h6" sx={styles.subHeading}>
          <I18N term="members-edit-sub_heading" />
        </Typography>
        <List>
          {subscribers.users.map((user) => (
            <SubscriberItem
              actor={user}
              idType="userId"
              removeSubscriber={removeSubscriber}
              key={user._id}
              isDisabled={subscription.isUserDisabled}
            />
          ))}
          {subscribers.freighties.map((freightie) => (
            <SubscriberItem
              actor={freightie}
              idType="freightieId"
              removeSubscriber={removeSubscriber}
              key={freightie._id}
              isDisabled
            />
          ))}
        </List>
      </DialogContent>
    </Dialog>
  ))
}

export interface SubscriberItemProps {
  actor: TActor
  idType: TIdType
  removeSubscriber: (id: string, idType: TIdType) => void
  isDisabled: boolean
}
export const SubscriberItem: FC<SubscriberItemProps> = ({ actor, removeSubscriber, idType, isDisabled }) => {
  const styles = getStyles(useTheme())

  const isValidThumbnailUrl = 'thumbnailUrl' in actor && actor.thumbnailUrl
  const avatar = isValidThumbnailUrl ? actor.thumbnailUrl : actor.initials
  const avatarType = isValidThumbnailUrl ? THUMBNAIL_AVATAR : INITIALS_AVATAR

  return (
    <ListItem sx={styles.listItem} data-testid={testIds.subscriberItem}>
      <ListItemAvatar>
        <SubscriptionAvatar
          disabled
          displayName={false}
          avatar={avatar}
          avatarType={avatarType}
          userType={getUserType(actor._id)}
        />
      </ListItemAvatar>
      <ListItemText primary={actor.name} secondary={actor.email} />
      <ListItemSecondaryAction>
        {!isDisabled && (
          <IconButton onClick={() => removeSubscriber(actor._id, idType)}>
            <CloseIcon />
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  )
}
